import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FileText,
  Eye,
  Lock,
  Unlock,
  Globe,
  Trash2,
  ExternalLink,
  Plus,
  Clock,
  Flame,
  Settings,
  LogOut,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import BackButton from './BackButton';
import './Dashboard.css';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

export default function Dashboard() {
  const { user, logout, getToken } = useAuth();
  const navigate = useNavigate();
  const [pastes, setPastes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pastes');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchPastes();
  }, [user, navigate]);

  const fetchPastes = async () => {
    try {
      const res = await fetch(`${API_URL}/my-pastes`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        setPastes(data.pastes);
      }
    } catch (err) {
      console.error('Failed to fetch pastes:', err);
    } finally {
      setLoading(false);
    }
  };

  const deletePaste = async (shortId) => {
    try {
      const res = await fetch(`${API_URL}/paste/${shortId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
        },
      });

      if (res.ok) {
        setPastes(pastes.filter(p => p.shortId !== shortId));
      }
    } catch (err) {
      console.error('Failed to delete paste:', err);
    }
    setDeleteConfirm(null);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getPrivacyIcon = (privacy) => {
    switch (privacy) {
      case 'private':
        return <Lock size={14} style={{ color: 'var(--accent-danger)' }} />;
      case 'public':
        return <Globe size={14} style={{ color: 'var(--accent-secondary)' }} />;
      default:
        return <Unlock size={14} style={{ color: 'var(--accent-warning)' }} />;
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!user) return null;

  return (
    <div className="dashboard-page">
      {/* Header */}
      <header className="dashboard-header">
        <div className="dashboard-header-left">
          <BackButton />
          <Link to="/" className="dashboard-logo">SCRAWL</Link>
        </div>
        <div className="dashboard-actions">
          <Link to="/" className="dashboard-btn primary">
            <Plus size={18} />
            New Paste
          </Link>
          <button onClick={handleLogout} className="dashboard-btn secondary">
            <LogOut size={18} />
            Sign Out
          </button>
        </div>
      </header>

      <div className="dashboard-content">
        {/* Sidebar */}
        <div className="dashboard-sidebar">
          {/* Profile Card */}
          <div className="profile-card">
            <div className="profile-info">
              <div className="profile-avatar">
                {user.username[0].toUpperCase()}
              </div>
              <div className="profile-details">
                <h2>@{user.username}</h2>
                <p>{user.email}</p>
              </div>
            </div>

            <div className="profile-stats">
              <div className="stat-box">
                <div className="number">{pastes.length}</div>
                <div className="label">Pastes</div>
              </div>
              <div className="stat-box">
                <div className="number">{user.totalViews || 0}</div>
                <div className="label">Views</div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="dashboard-nav">
            <button
              onClick={() => setActiveTab('pastes')}
              className={`nav-btn ${activeTab === 'pastes' ? 'active' : ''}`}
            >
              <FileText size={18} />
              My Pastes
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`nav-btn ${activeTab === 'settings' ? 'active' : ''}`}
            >
              <Settings size={18} />
              Settings
            </button>
          </nav>

          {/* Free Badge */}
          <div className="free-badge">
            <h4>✨ Free Account</h4>
            <p>All premium features unlocked!</p>
          </div>
        </div>

        {/* Main Content */}
        <div className="dashboard-main">
          {activeTab === 'pastes' && (
            <>
              <div className="panel-header">
                <h2>My Pastes</h2>
                <span>{pastes.length} total</span>
              </div>

              {loading ? (
                <div className="loading-state">Loading your pastes...</div>
              ) : pastes.length === 0 ? (
                <div className="empty-state">
                  <FileText size={48} />
                  <p>You haven't created any pastes yet</p>
                  <Link to="/" className="dashboard-btn primary">
                    <Plus size={18} />
                    Create Your First Paste
                  </Link>
                </div>
              ) : (
                <div className="paste-list">
                  {pastes.map((paste) => (
                    <div key={paste.shortId} className="paste-item">
                      <div className="paste-info">
                        <div className="paste-title-row">
                          {getPrivacyIcon(paste.privacy)}
                          <Link to={`/p/${paste.shortId}`} className="paste-title">
                            {paste.title}
                          </Link>
                          {paste.burnAfterRead && (
                            <Flame size={14} style={{ color: 'var(--accent-warning)' }} />
                          )}
                        </div>
                        <div className="paste-meta">
                          <span>
                            <Clock size={12} />
                            {formatDate(paste.createdAt)}
                          </span>
                          <span>
                            <Eye size={12} />
                            {paste.viewCount} views
                          </span>
                          <span className="paste-lang">{paste.language}</span>
                        </div>
                      </div>

                      <div className="paste-actions">
                        <Link to={`/p/${paste.shortId}`} className="action-btn" title="View paste">
                          <ExternalLink size={16} />
                        </Link>
                        <button
                          onClick={() => setDeleteConfirm(paste.shortId)}
                          className="action-btn delete"
                          title="Delete paste"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>

                      {/* Delete Confirmation Modal */}
                      {deleteConfirm === paste.shortId && (
                        <div className="modal-overlay">
                          <div className="modal-content">
                            <h3>Delete Paste?</h3>
                            <p>This will permanently delete "{paste.title}". This action cannot be undone.</p>
                            <div className="modal-actions">
                              <button className="confirm" onClick={() => deletePaste(paste.shortId)}>
                                Delete
                              </button>
                              <button className="cancel" onClick={() => setDeleteConfirm(null)}>
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {activeTab === 'settings' && (
            <div className="settings-panel">
              <h2>Account Settings</h2>

              <div className="settings-form">
                <div className="form-group">
                  <label className="form-label">Display Name</label>
                  <input
                    type="text"
                    defaultValue={user.displayName || ''}
                    placeholder={user.username}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Bio</label>
                  <textarea
                    defaultValue={user.bio || ''}
                    placeholder="Tell us about yourself..."
                    rows={3}
                    className="form-input"
                    style={{ resize: 'none' }}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Default Privacy</label>
                  <select defaultValue={user.defaultPrivacy || 'unlisted'} className="form-input">
                    <option value="public">Public - Visible to everyone</option>
                    <option value="unlisted">Unlisted - Only with link</option>
                    <option value="private">Private - Only you</option>
                  </select>
                </div>

                <button className="save-btn">Save Changes</button>
              </div>

              <div className="danger-zone">
                <h3>Danger Zone</h3>
                <button className="danger-btn">Delete Account</button>
                <p className="danger-hint">
                  This will permanently delete your account and all your pastes.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
